import { useMemo, useState } from 'react';
import { useLingui } from '@lingui/react/macro';
import debounce from 'lodash/debounce';

import { InputSelect } from '@/design_system/InputSelect/InputSelect';
import Stack from '@/design_system/Stack';
import TextArea from '@/design_system/TextArea';
import Toggle from '@/design_system/Toggle';
import { REQUEST_WAIVE_HANDLING_FEE_REASONS, useUpdateDraftRequest } from '@/models/request';

import { useRequestContext } from '../../contexts/RequestContext';

export const ApplyHandlingFee = () => {
  const { t, i18n } = useLingui();
  const { request, view, errors } = useRequestContext();

  const { mutate: updateDraftRequest } = useUpdateDraftRequest(request.id);

  const debouncedUpdateDraftRequest = useMemo(() => {
    return debounce(updateDraftRequest, 500);
  }, [updateDraftRequest]);

  const [otherReason, setOtherReason] = useState(request.waiveHandlingFeeReason?.otherReason ?? '');

  if (!view.organizationPrice.shown) {
    return null;
  }

  const handlingFee = request.fees.find((fee) => fee.feeType.slug === 'client-handling');

  if (
    (!handlingFee || handlingFee.organizationPrice?.amountPerCurrency?.[0]?.amount === 0) &&
    !request.waiveHandlingFee
  ) {
    return null;
  }

  return (
    <Stack gap="0.5rem">
      <Toggle
        label={t({
          id: 'requests.apply-handling-fee-toggle.label',
          message: 'Apply handling fee',
        })}
        alignment="left"
        isSelected={!request.waiveHandlingFee}
        onChange={(value) => {
          updateDraftRequest({ waiveHandlingFee: !value });
        }}
      />
      {!!request.waiveHandlingFee && (
        <>
          <InputSelect
            variant="select"
            aria-label={t({
              id: 'requests.apply-handling-fee-toggle.select-placeholder',
              message: 'Select a reason',
            })}
            placeholder={t({
              id: 'requests.apply-handling-fee-toggle.select-placeholder',
              message: 'Select a reason',
            })}
            options={REQUEST_WAIVE_HANDLING_FEE_REASONS}
            getOptionValue={(option) => option.id}
            getOptionLabel={(option) => i18n._(option.label)}
            value={REQUEST_WAIVE_HANDLING_FEE_REASONS.find(
              (reason) => reason.id === request.waiveHandlingFeeReason?.reason
            )}
            onChange={(option) => {
              if (option) {
                updateDraftRequest({ waiveHandlingFeeReason: { reason: option.id } });
              }
            }}
            isSearchable={false}
            isClearable={false}
            error={
              errors?.price?.missingWaiveHandlingFeeReason
                ? t({
                    id: 'requests.apply-handling-fee-toggle.error',
                    message: 'Please select a reason for waiving the handling fee',
                  })
                : undefined
            }
          />
          {request.waiveHandlingFeeReason?.reason === 'other' && (
            <TextArea
              label={t({
                id: 'requests.apply-handling-fee-toggle.other-reason-label',
                message: 'Specify the reason',
              })}
              placeholder={t({
                id: 'requests.apply-handling-fee-toggle.other-reason-label',
                message: 'Specify the reason',
              })}
              value={otherReason}
              onChange={(e) => {
                setOtherReason(e.target.value);
                debouncedUpdateDraftRequest({
                  waiveHandlingFeeReason: { reason: 'other', otherReason: e.target.value },
                });
              }}
              error={
                errors?.price?.missingWaiveHandlingFeeOtherReason
                  ? t({
                      id: 'requests.apply-handling-fee-toggle.other-reason-error',
                      message: 'Please specify the reason for waiving the handling fee',
                    })
                  : undefined
              }
            />
          )}
        </>
      )}
    </Stack>
  );
};
